import React from "react";
import {
  brandLinkTargetProps,
  getBrandLinksForPlacement,
  resolveBrandLinkIconClass,
  resolveBrandLinkIconUrl,
  applySharedPlatformBrandIcons,
} from "./brandLinks";
import { normalizeExternalUrl, formatExternalUrlForDisplay } from "./urls";
import { resolveMediaUrl, handleMediaImgError } from "./mediaUrl";
import { normalizeSocialNetworkKey } from "./socialNetworks";
import "./BrandLinksRow.css";

/**
 * Renders Brand Links / entity social links.
 * Uploaded Brand Link artwork renders exactly as uploaded (no white circle/border).
 * Builtin FA icons keep the legacy circle stack for glyph contrast.
 * Entity-provided links reuse shared Pentecost+ Brand Link icon artwork;
 * only destination URLs differ per entity.
 */
function BrandLinksRow({
  placement = "footer",
  links: linksProp,
  className = "social-share",
  stackClassName = "fa-stack fa-lg brand-link-stack",
  circleClassName = "fas fa-circle fa-stack-2x brand-link-circle",
}) {
  const rawLinks = Array.isArray(linksProp)
    ? linksProp.filter(
        (link) =>
          link &&
          link.url &&
          link.is_enabled !== false &&
          link.is_visible !== false
      )
    : getBrandLinksForPlacement(placement);

  const links = Array.isArray(linksProp)
    ? applySharedPlatformBrandIcons(rawLinks)
    : rawLinks;

  if (!links.length) {
    return null;
  }

  const keyPrefix = Array.isArray(linksProp) ? "provided" : placement;

  const items = links
    .map((link) => {
      const customIconUrl = resolveBrandLinkIconUrl(link);
      const iconClass = customIconUrl
        ? null
        : resolveBrandLinkIconClass(link);
      const targetProps = brandLinkTargetProps(link);
      const displayUrl = formatExternalUrlForDisplay(link.url);
      const aria =
        link.aria_label ||
        link.tooltip ||
        link.display_name ||
        displayUrl ||
        "Brand link";
      const networkClass = normalizeNetworkClass(
        link.css_class || link.network || link.display_name
      );
      const href = normalizeExternalUrl(link.url);
      if (!href) {
        return null;
      }
      const title =
        link.tooltip || link.display_name || displayUrl || undefined;

      return (
        <a
          key={`brand-link-${keyPrefix}-${link.id}`}
          href={href}
          title={title}
          aria-label={aria}
          data-analytics-id={link.analytics_id || undefined}
          className={
            "brand-link-item" + (networkClass ? " " + networkClass : "")
          }
          {...targetProps}
        >
          {customIconUrl ? (
            <img
              src={resolveMediaUrl(customIconUrl, customIconUrl)}
              alt=""
              className="brand-link-custom-icon"
              onError={handleMediaImgError}
            />
          ) : (
            <span className={stackClassName}>
              <i
                className={
                  circleClassName +
                  (networkClass ? " " + networkClass : "")
                }
                aria-hidden="true"
              />
              <i
                className={
                  (iconClass || "fas fa-link") +
                  " fa-stack-1x fa-inverse brand-link-glyph"
                }
                aria-hidden="true"
              />
            </span>
          )}
        </a>
      );
    })
    .filter(Boolean);

  if (!items.length) {
    return null;
  }

  if (!className) {
    return <>{items}</>;
  }

  return <div className={className}>{items}</div>;
}

function normalizeNetworkClass(raw) {
  const canonical = normalizeSocialNetworkKey(raw);
  if (!canonical || canonical === "link") {
    return "";
  }
  // Keep legacy twitter class for X network colors in older CSS.
  if (canonical === "x") {
    return "x twitter";
  }
  return canonical;
}

export default BrandLinksRow;
